import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { 
  DndContext, 
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Folder, 
  FolderOpen, 
  Plus, 
  Edit2, 
  Trash2, 
  GripVertical,
  ChevronRight,
  ChevronDown,
  Home
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Draggable Category Item Component
const CategoryItem = ({ category, onEdit, onDelete, isOver, isSubcategory }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const parentName = category.parent_id ? 
    `Alt Kategori (${category.parent_name || 'Unknown'})` : 
    'Ana Kategori';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        flex items-center gap-3 p-4 bg-white border rounded-lg mb-2
        ${isDragging ? 'shadow-2xl border-red-500' : 'border-gray-200 hover:border-red-300'}
        ${isOver ? 'bg-red-50' : ''}
        ${isSubcategory ? 'ml-8 bg-gray-50' : ''}
      `}
    >
      {/* Drag Handle */}
      <div 
        {...attributes} 
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded"
      >
        <GripVertical size={20} className="text-gray-400" />
      </div>

      {/* Icon */}
      <div className="flex-shrink-0">
        {category.parent_id ? (
          <ChevronRight size={20} className="text-gray-400" />
        ) : (
          <Folder size={24} className="text-red-600" />
        )}
      </div>

      {/* Category Info */}
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900">{category.name}</h3>
          <span className={`px-2 py-1 text-xs rounded-full ${
            category.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
          }`}>
            {category.enabled ? 'Aktif' : 'Pasif'}
          </span>
        </div>
        <p className="text-sm text-gray-500">{parentName} • Slug: {category.slug}</p>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={() => onEdit(category)}
          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          title="Düzenle"
        >
          <Edit2 size={18} />
        </button>
        <button
          onClick={() => onDelete(category)}
          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          title="Sil"
        >
          <Trash2 size={18} />
        </button>
      </div>
    </div>
  );
};

const AdminCategories = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    parent_id: null,
    enabled: true,
    order: 999
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/categories`);
      const data = await response.json();
      
      // Flatten categories for easier drag & drop
      const flattenCategories = (cats, parentName = null) => {
        let flat = [];
        cats.forEach(cat => {
          flat.push({ ...cat, parent_name: parentName });
          if (cat.subcategories && cat.subcategories.length > 0) {
            flat = flat.concat(flattenCategories(cat.subcategories, cat.name));
          }
        });
        return flat;
      };
      
      const allCategories = flattenCategories(data);
      
      // Sort by order field
      allCategories.sort((a, b) => (a.order || 999) - (b.order || 999));
      
      setCategories(allCategories);
    } catch (error) {
      console.error('Kategori yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      setActiveId(null);
      return;
    }

    const draggedCategory = categories.find(c => c.id === active.id);
    const targetCategory = categories.find(c => c.id === over.id);

    if (!draggedCategory || !targetCategory) {
      setActiveId(null);
      return;
    }

    // Check if both are at same level (both main or both sub with same parent)
    const sameLevel = draggedCategory.parent_id === targetCategory.parent_id;

    if (sameLevel) {
      // Reordering within the same level
      const sameLevelCategories = categories.filter(c => 
        c.parent_id === draggedCategory.parent_id
      );
      
      const oldIndex = sameLevelCategories.findIndex(c => c.id === draggedCategory.id);
      const newIndex = sameLevelCategories.findIndex(c => c.id === targetCategory.id);

      if (oldIndex === newIndex) {
        setActiveId(null);
        return;
      }

      // Reorder array
      const reorderedCategories = [...sameLevelCategories];
      const [movedItem] = reorderedCategories.splice(oldIndex, 1);
      reorderedCategories.splice(newIndex, 0, movedItem);

      // Update order field for all affected categories
      try {
        const token = localStorage.getItem('adminToken');
        const updates = reorderedCategories.map((cat, index) => ({
          id: cat.id,
          order: index + 1
        }));

        // Send batch update
        for (const update of updates) {
          const cat = categories.find(c => c.id === update.id);
          await fetch(`${API_URL}/api/admin/categories/${update.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              ...cat,
              order: update.order
            })
          });
        }

        fetchCategories();
      } catch (error) {
        console.error('Reorder error:', error);
        alert('❌ Sıralama güncellenemedi!');
      }

      setActiveId(null);
      return;
    }

    // Different levels - changing parent
    // Prevent dropping parent into its own child
    if (targetCategory.parent_id === draggedCategory.id) {
      alert('❌ Ana kategoriyi kendi alt kategorisinin içine taşıyamazsınız!');
      setActiveId(null);
      return;
    }

    // Update parent_id: drop onto another category makes it a subcategory
    // Ensure parent_id is always integer or null
    const newParentId = targetCategory.parent_id || targetCategory.id;
    const newParentIdInt = newParentId === draggedCategory.id ? null : (typeof newParentId === 'number' ? newParentId : parseInt(newParentId, 10));
    
    const confirmMessage = newParentIdInt === null
      ? `"${draggedCategory.name}" kategorisini ana kategori yapmak istiyor musunuz?`
      : `"${draggedCategory.name}" kategorisini "${targetCategory.name}" kategorisinin altına taşımak istiyor musunuz?`;

    if (!window.confirm(confirmMessage)) {
      setActiveId(null);
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/categories/${draggedCategory.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...draggedCategory,
          parent_id: newParentIdInt
        })
      });

      if (response.ok) {
        alert('✅ Kategori başarıyla taşındı!');
        fetchCategories();
      } else {
        alert('❌ Kategori taşınamadı!');
      }
    } catch (error) {
      console.error('Drag error:', error);
      alert('❌ Bir hata oluştu!');
    }

    setActiveId(null);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (response.ok) {
        alert('✅ Kategori başarıyla oluşturuldu!');
        setShowAddModal(false);
        resetForm();
        fetchCategories();
      } else {
        const error = await response.json();
        alert('❌ Hata: ' + (error.detail || 'Kategori oluşturulamadı'));
      }
    } catch (error) {
      console.error('Kategori oluşturma hatası:', error);
      alert('❌ Bir hata oluştu!');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/categories/${editingCategory.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (response.ok) {
        alert('✅ Kategori başarıyla güncellendi!');
        setShowEditModal(false);
        setEditingCategory(null);
        resetForm();
        fetchCategories();
      } else {
        const error = await response.json();
        alert('❌ Hata: ' + (error.detail || 'Kategori güncellenemedi'));
      }
    } catch (error) {
      console.error('Kategori güncelleme hatası:', error);
      alert('❌ Bir hata oluştu!');
    }
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`"${category.name}" kategorisini silmek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/categories/${category.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (response.ok) {
        alert('✅ Kategori başarıyla silindi!');
        fetchCategories();
      } else {
        const error = await response.json();
        alert('❌ Hata: ' + (error.detail || 'Kategori silinemedi'));
      }
    } catch (error) {
      console.error('Kategori silme hatası:', error);
      alert('❌ Bir hata oluştu!');
    }
  };

  const handleMakeMainCategory = async (category) => {
    if (!window.confirm(`"${category.name}" kategorisini ana kategori yapmak istiyor musunuz?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/categories/${category.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...category,
          parent_id: null
        })
      });

      if (response.ok) {
        alert('✅ Kategori ana kategoriye çevrildi!');
        fetchCategories();
      } else {
        alert('❌ İşlem başarısız!');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('❌ Bir hata oluştu!');
    }
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      parent_id: category.parent_id,
      enabled: category.enabled,
      order: category.order || 999
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      parent_id: null,
      enabled: true,
      order: 999
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Yükleniyor...</div>
        </div>
      </AdminLayout>
    );
  }

  const mainCategories = categories.filter(c => !c.parent_id).sort((a, b) => (a.order || 999) - (b.order || 999));
  const subCategories = categories.filter(c => c.parent_id).sort((a, b) => (a.order || 999) - (b.order || 999));

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Kategoriler</h1>
            <p className="text-gray-600 mt-1">Kategorileri sürükleyerek yeniden düzenleyin</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-semibold"
          >
            <Plus size={20} />
            Yeni Kategori
          </button>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex gap-2">
            <GripVertical size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900">Nasıl Kullanılır?</h3>
              <ul className="text-sm text-blue-800 mt-1 space-y-1">
                <li>• <strong>Sıralama:</strong> Ana kategoriler içinde sürükleyerek sıralamayı değiştirin</li>
                <li>• <strong>Alt kategori yapma:</strong> Bir kategoriyi başka kategorinin üzerine bırakın</li>
                <li>• <strong>Ana kategori yapma:</strong> Düzenle butonuyla alt kategorileri ana kategoriye çevirin</li>
                <li>• Kategorileri <GripVertical size={14} className="inline" /> işaretinden tutarak sürükleyin</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Categories with Drag & Drop */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="bg-gray-50 rounded-lg p-6">
            {/* Main Categories */}
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Home size={20} />
                Ana Kategoriler ({mainCategories.length})
              </h2>
              <SortableContext
                items={mainCategories.map(c => c.id)}
                strategy={verticalListSortingStrategy}
              >
                {mainCategories.map(category => (
                  <CategoryItem
                    key={category.id}
                    category={category}
                    onEdit={openEditModal}
                    onDelete={handleDelete}
                    isSubcategory={false}
                  />
                ))}
              </SortableContext>
            </div>

            {/* Sub Categories */}
            {subCategories.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <ChevronRight size={20} />
                  Alt Kategoriler ({subCategories.length})
                </h2>
                <SortableContext
                  items={subCategories.map(c => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {subCategories.map(category => (
                    <CategoryItem
                      key={category.id}
                      category={category}
                      onEdit={openEditModal}
                      onDelete={handleDelete}
                      isSubcategory={true}
                    />
                  ))}
                </SortableContext>
              </div>
            )}
          </div>

          <DragOverlay>
            {activeId ? (
              <div className="p-4 bg-white border-2 border-red-500 rounded-lg shadow-2xl opacity-90">
                <div className="flex items-center gap-3">
                  <GripVertical size={20} className="text-gray-400" />
                  <Folder size={24} className="text-red-600" />
                  <span className="font-semibold">
                    {categories.find(c => c.id === activeId)?.name}
                  </span>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>

        {/* Add Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
              <h2 className="text-2xl font-bold mb-4">Yeni Kategori Ekle</h2>
              <form onSubmit={handleCreate}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Kategori Adı
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Slug (URL)
                    </label>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData({...formData, slug: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Üst Kategori
                    </label>
                    <select
                      value={formData.parent_id || ''}
                      onChange={(e) => setFormData({...formData, parent_id: e.target.value || null})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                    >
                      <option value="">Ana Kategori</option>
                      {mainCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.enabled}
                      onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
                      className="mr-2"
                    />
                    <label className="text-sm font-medium text-gray-700">Aktif</label>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    type="submit"
                    className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition-colors font-semibold"
                  >
                    Kaydet
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      resetForm();
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                  >
                    İptal
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
              <h2 className="text-2xl font-bold mb-4">Kategori Düzenle</h2>
              <form onSubmit={handleUpdate}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Kategori Adı
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Slug (URL)
                    </label>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData({...formData, slug: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Üst Kategori
                    </label>
                    <select
                      value={formData.parent_id || ''}
                      onChange={(e) => setFormData({...formData, parent_id: e.target.value || null})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                    >
                      <option value="">Ana Kategori</option>
                      {mainCategories
                        .filter(cat => cat.id !== editingCategory?.id) // Can't be its own parent
                        .map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                    </select>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.enabled}
                      onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
                      className="mr-2"
                    />
                    <label className="text-sm font-medium text-gray-700">Aktif</label>
                  </div>

                  {/* Quick Action: Make Main Category */}
                  {editingCategory?.parent_id && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <button
                        type="button"
                        onClick={() => {
                          handleMakeMainCategory(editingCategory);
                          setShowEditModal(false);
                        }}
                        className="text-sm text-blue-700 font-semibold hover:text-blue-900"
                      >
                        🏠 Ana Kategoriye Çevir
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    type="submit"
                    className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition-colors font-semibold"
                  >
                    Güncelle
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingCategory(null);
                      resetForm();
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                  >
                    İptal
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCategories;
